// MARK://dhk8/pos/lib/clients/customers
// Typed client for fiscal customers (CFDI receivers).
//
// 2026-05-26 (Lane #178.fix) — apunta al MISMO servicio que admin-shell:
// `dhk8-crm-contacts` `/api/contacts`. Antes la página POS leía de
// `dhk8-erp-customers /v1/customers` (servicio diferente) y por eso el listado
// de admin `/customers/fiscal` y el del POS NO compartían dataset: lo que el
// staff capturaba en uno no aparecía en el otro.
//
// El modelo CRM es: 1 Contact (persona) con N FiscalEntity[] (razones
// sociales). Este cliente proyecta cada par (Contact, FiscalEntity) como UN
// CustomerDto plano que la UI del POS y los flows fiscales pueden consumir
// sin saber del modelo CRM anidado. El `id` del CustomerDto es compound:
// `<contactId>#<fiscalEntityId>` para que getCustomer/updateCustomer/delete
// puedan apuntar a la fiscalEntity exacta.

import { getTenantId } from '../api.js'
import { gqlClient } from '../gql.js'
import {
  PosContactsQuery,
  PosContactQuery,
  PosCreateContactMutation,
  PosUpdateContactMutation,
} from '../queries/contacts.queries.js'

// 2026-05-24: default disabled (era '1' = always-on que mostraba customers
// hardcoded incluso en prod). Activar con VITE_BILLING_STUB=1 si dev local.
const STUB_ENABLED = ((): boolean => {
  try {
    return (import.meta.env.VITE_BILLING_STUB ?? '0') === '1'
  } catch {
    return false
  }
})()

// ── DTOs (mirror of sc-restaurant CustomerRow) ─────────────────────────────

export interface CustomerDto {
  id: string
  tenantId: string
  rfc: string
  legalName: string
  tradeName: string | null
  email: string | null
  phone: string | null
  zipCode: string | null
  defaultCfdiUsage: string | null
  defaultPaymentForm: string | null
  fiscalRegimeCode: string | null
}

export interface CustomerInput {
  rfc: string
  legalName: string
  tradeName?: string | null
  email?: string | null
  phone?: string | null
  zipCode: string
  defaultCfdiUsage?: string | null
  defaultPaymentForm?: string | null
  fiscalRegimeCode: string
}

// ── Stub ───────────────────────────────────────────────────────────────────

let stub: CustomerDto[] | null = null

function seed(): CustomerDto[] {
  return [
    {
      id: 'cus_001',
      tenantId: 't0',
      rfc: 'XAXX010101000',
      legalName: 'PÚBLICO EN GENERAL',
      tradeName: null,
      email: null,
      phone: null,
      zipCode: '03100',
      defaultCfdiUsage: 'S01',
      defaultPaymentForm: '01',
      fiscalRegimeCode: '616',
    },
    {
      id: 'cus_002',
      tenantId: 't0',
      rfc: 'GODE561231GR8',
      legalName: 'Gustavo Durán Buenrostro',
      tradeName: null,
      email: 'gustavo@example.mx',
      phone: '+525511223344',
      zipCode: '01219',
      defaultCfdiUsage: 'G03',
      defaultPaymentForm: '03',
      fiscalRegimeCode: '612',
    },
    {
      id: 'cus_003',
      tenantId: 't0',
      rfc: 'SCM150525XY1',
      legalName: 'Servicios Creativos México SA de CV',
      tradeName: 'Servicios Creativos',
      email: 'admin@servicioscreativos.mx',
      phone: '+525500000000',
      zipCode: '03100',
      defaultCfdiUsage: 'G03',
      defaultPaymentForm: '03',
      fiscalRegimeCode: '601',
    },
  ]
}

function getStub(): CustomerDto[] {
  if (!stub) stub = seed()
  return stub
}

// ── API ────────────────────────────────────────────────────────────────────

// ── Adapter Contact+FiscalEntity → CustomerDto ─────────────────────────────
// Modelo CRM (dhk8-crm-contacts): un Contact con N fiscalEntities[].
// Modelo legacy POS (`CustomerDto`): plano (1 Contact con 1 fiscalEntity).
// Proyectamos cada (Contact, FiscalEntity) como UN CustomerDto con id compound.

interface CrmFiscalEntity {
  id: string
  rfc: string
  legalName: string
  regimeCode: string
  zipCode: string
  usoCfdi?: string | null
  invoiceEmail?: string | null
  isDefault?: boolean
}

interface CrmEmail { value: string }
interface CrmPhone { value: string }

interface CrmContact {
  id: string
  tenantId: string
  firstName?: string
  lastName?: string | null
  fullName?: string
  emails?: CrmEmail[]
  phones?: CrmPhone[]
  fiscalEntities?: CrmFiscalEntity[]
}

const COMPOUND_SEP = '#'

// El grafo devuelve emails {email} / phones {phone}; el adapter interno usa
// {value}. Normalizamos la forma del grafo a la CrmContact que adaptContactFiscal
// espera (sin tocar la lógica de aplanado existente).
interface GraphContact {
  id: string
  tenantId: string
  firstName?: string | null
  lastName?: string | null
  fullName?: string | null
  emails?: Array<{ email: string }> | null
  phones?: Array<{ phone: string }> | null
  fiscalEntities?: CrmFiscalEntity[] | null
}
function graphToCrmContact(g: GraphContact): CrmContact {
  return {
    id: g.id,
    tenantId: g.tenantId,
    firstName: g.firstName ?? undefined,
    lastName: g.lastName ?? null,
    fullName: g.fullName ?? undefined,
    emails: (g.emails ?? []).map((e) => ({ value: e.email })),
    phones: (g.phones ?? []).map((p) => ({ value: p.phone })),
    fiscalEntities: g.fiscalEntities ?? [],
  }
}

function makeCompoundId(contactId: string, fiscalEntityId: string): string {
  return `${contactId}${COMPOUND_SEP}${fiscalEntityId}`
}

function splitCompoundId(id: string): { contactId: string; fiscalEntityId: string | null } {
  const idx = id.indexOf(COMPOUND_SEP)
  if (idx < 0) return { contactId: id, fiscalEntityId: null }
  return { contactId: id.slice(0, idx), fiscalEntityId: id.slice(idx + 1) }
}

function adaptContactFiscal(contact: CrmContact, fe: CrmFiscalEntity): CustomerDto {
  const primaryEmail = fe.invoiceEmail ?? contact.emails?.[0]?.value ?? null
  const primaryPhone = contact.phones?.[0]?.value ?? null
  // tradeName: si Contact tiene fullName diferente al legalName fiscal, lo
  // exponemos como "nombre de pila" del cliente; útil cuando la persona
  // física se llama "Juan Pérez" pero su razón social es otro string.
  const personName = contact.fullName
    ?? [contact.firstName, contact.lastName].filter(Boolean).join(' ').trim()
    ?? null
  const tradeName = personName && personName !== fe.legalName ? personName : null
  return {
    id: makeCompoundId(contact.id, fe.id),
    tenantId: contact.tenantId,
    rfc: fe.rfc,
    legalName: fe.legalName,
    tradeName,
    email: primaryEmail,
    phone: primaryPhone,
    zipCode: fe.zipCode,
    defaultCfdiUsage: fe.usoCfdi ?? null,
    defaultPaymentForm: null, // CRM no lo guarda hoy
    fiscalRegimeCode: fe.regimeCode,
  }
}

/** Aplana lista de Contacts → CustomerDto[]. Contacts sin fiscalEntities se omiten. */
function flattenContacts(contacts: CrmContact[]): CustomerDto[] {
  const out: CustomerDto[] = []
  for (const c of contacts) {
    if (!c.fiscalEntities || c.fiscalEntities.length === 0) continue
    for (const fe of c.fiscalEntities) out.push(adaptContactFiscal(c, fe))
  }
  return out
}

export async function listCustomers(search?: string, signal?: AbortSignal): Promise<CustomerDto[]> {
  if (STUB_ENABLED) {
    const all = getStub()
    if (!search) return all
    const q = search.toLowerCase()
    return all.filter(
      (c) =>
        c.rfc.toLowerCase().includes(q) ||
        c.legalName.toLowerCase().includes(q) ||
        (c.tradeName ?? '').toLowerCase().includes(q),
    )
  }
  // F9.12 / ADR-0009: lectura por el grafo (crm-contacts, tenant del JWT).
  const ctx = signal ? { fetchOptions: { signal } } : undefined
  const vars = search ? { limit: 200, q: search } : { limit: 200 }
  const res = await gqlClient.query(PosContactsQuery, vars, ctx).toPromise()
  if (res.error) throw res.error
  const items = (res.data?.contacts.items ?? []).map((c) => graphToCrmContact(c as GraphContact))
  return flattenContacts(items)
}

export async function getCustomer(id: string, signal?: AbortSignal): Promise<CustomerDto | null> {
  if (STUB_ENABLED) return getStub().find((c) => c.id === id) ?? null
  const { contactId, fiscalEntityId } = splitCompoundId(id)
  const ctx = signal ? { fetchOptions: { signal } } : undefined
  const res = await gqlClient.query(PosContactQuery, { id: contactId }, ctx).toPromise()
  if (res.error) throw res.error
  const contact = res.data?.contact ? graphToCrmContact(res.data.contact as GraphContact) : null
  if (contact === null || !contact.fiscalEntities || contact.fiscalEntities.length === 0) return null
  const fe = fiscalEntityId
    ? contact.fiscalEntities.find((f) => f.id === fiscalEntityId)
    : (contact.fiscalEntities.find((f) => f.isDefault) ?? contact.fiscalEntities[0])
  if (!fe) return null
  return adaptContactFiscal(contact, fe)
}

/**
 * Crear cliente fiscal nuevo (1 Contact + 1 FiscalEntity).
 * El servicio CRM exige `firstName` no vacío. Usamos `legalName` como
 * fallback cuando el caller no lo separa explícitamente — para personas
 * morales el `firstName` queda como la razón social, que es lo que aparece
 * en el listing.
 */
export async function createCustomer(input: CustomerInput): Promise<CustomerDto> {
  if (STUB_ENABLED) {
    const list = getStub()
    const c: CustomerDto = {
      id: `cus_${Math.random().toString(36).slice(2, 8)}`,
      tenantId: getTenantId() ?? 't0',
      rfc: input.rfc.toUpperCase(),
      legalName: input.legalName,
      tradeName: input.tradeName ?? null,
      email: input.email ?? null,
      phone: input.phone ?? null,
      zipCode: input.zipCode,
      defaultCfdiUsage: input.defaultCfdiUsage ?? null,
      defaultPaymentForm: input.defaultPaymentForm ?? null,
      fiscalRegimeCode: input.fiscalRegimeCode,
    }
    stub = [c, ...list]
    return c
  }
  // Si el caller pasó tradeName, lo usamos como "persona" del contact
  // (firstName/lastName), dejando legalName SÓLO en la fiscalEntity.
  const personName = input.tradeName?.trim() || input.legalName.trim()
  // F9.12 / ADR-0009: alta por el grafo (crm-contacts, tenant del JWT, guard IDOR).
  const res = await gqlClient.mutation(PosCreateContactMutation, {
    input: {
      firstName: personName,
      ...(input.email ? { emails: [{ email: input.email }] } : {}),
      ...(input.phone ? { phones: [{ phone: input.phone }] } : {}),
      fiscalEntities: [{
        rfc: input.rfc.trim().toUpperCase(),
        legalName: input.legalName.trim(),
        regimeCode: input.fiscalRegimeCode,
        zipCode: input.zipCode,
        usoCfdi: input.defaultCfdiUsage ?? 'G03',
        ...(input.email ? { invoiceEmail: input.email } : {}),
        isDefault: true,
      }],
    },
  }).toPromise()
  if (res.error) throw res.error
  const createdRaw = res.data?.createContact
  if (!createdRaw) throw new Error('createCustomer: el grafo no devolvió el contacto')
  const created = graphToCrmContact(createdRaw as GraphContact)
  const fe = created.fiscalEntities?.[0]
  if (!fe) throw new Error('createCustomer: el grafo devolvió contact sin fiscalEntity')
  return adaptContactFiscal(created, fe)
}

export async function updateCustomer(id: string, patch: Partial<CustomerInput>): Promise<CustomerDto> {
  if (STUB_ENABLED) {
    const list = getStub()
    const idx = list.findIndex((c) => c.id === id)
    if (idx === -1) throw new Error(`Customer ${id} not found`)
    const merged: CustomerDto = { ...list[idx]!, ...(patch as Partial<CustomerDto>) }
    stub = list.map((c) => (c.id === id ? merged : c))
    return merged
  }
  const { contactId, fiscalEntityId } = splitCompoundId(id)
  // Para actualizar UNA fiscalEntity dentro del contact, leemos la lista completa
  // por el grafo, reemplazamos la entry target, y la mandamos entera. La mutation
  // updateContact acepta `fiscalEntities` como reemplazo completo (sync set).
  const cur = await gqlClient.query(PosContactQuery, { id: contactId }).toPromise()
  if (cur.error) throw cur.error
  const current = cur.data?.contact ? graphToCrmContact(cur.data.contact as GraphContact) : null
  if (current === null) throw new Error(`Contact ${contactId} not found`)
  const fiscals = current.fiscalEntities ?? []
  const targetIdx = fiscalEntityId
    ? fiscals.findIndex((f) => f.id === fiscalEntityId)
    : fiscals.findIndex((f) => f.isDefault) === -1 ? 0 : fiscals.findIndex((f) => f.isDefault)
  if (targetIdx < 0) throw new Error(`FiscalEntity ${fiscalEntityId ?? '(default)'} not found in contact ${contactId}`)
  const oldFe = fiscals[targetIdx]!
  const newFe: CrmFiscalEntity = {
    ...oldFe,
    ...(patch.rfc ? { rfc: patch.rfc.trim().toUpperCase() } : {}),
    ...(patch.legalName ? { legalName: patch.legalName.trim() } : {}),
    ...(patch.fiscalRegimeCode ? { regimeCode: patch.fiscalRegimeCode } : {}),
    ...(patch.zipCode ? { zipCode: patch.zipCode } : {}),
    ...(patch.defaultCfdiUsage !== undefined ? { usoCfdi: patch.defaultCfdiUsage } : {}),
    ...(patch.email !== undefined ? { invoiceEmail: patch.email } : {}),
  }
  const nextFiscals = fiscals.map((f, i) => (i === targetIdx ? newFe : f))
  const body: Record<string, unknown> = { fiscalEntities: nextFiscals }
  // Sincronizar también campos del Contact si el patch los toca.
  if (patch.tradeName !== undefined) body['firstName'] = patch.tradeName || newFe.legalName
  if (patch.email !== undefined) body['emails'] = patch.email ? [{ email: patch.email }] : []
  if (patch.phone !== undefined) body['phones'] = patch.phone ? [{ phone: patch.phone }] : []
  const res = await gqlClient.mutation(PosUpdateContactMutation, { id: contactId, input: body }).toPromise()
  if (res.error) throw res.error
  const updatedRaw = res.data?.updateContact
  if (!updatedRaw) throw new Error('updateCustomer: el grafo no devolvió el contacto')
  const updated = graphToCrmContact(updatedRaw as GraphContact)
  const fe = updated.fiscalEntities?.find((f) => f.id === newFe.id) ?? updated.fiscalEntities?.[targetIdx]
  if (!fe) throw new Error('updateCustomer: el grafo devolvió contact sin la fiscalEntity esperada')
  return adaptContactFiscal(updated, fe)
}

export async function deleteCustomer(id: string): Promise<void> {
  if (STUB_ENABLED) {
    stub = getStub().filter((c) => c.id !== id)
    return
  }
  // Borrar UNA fiscalEntity (no el Contact entero — preserva datos de
  // lealtad/CRM). Si era la única fiscalEntity, el Contact queda sin entidades
  // fiscales (CRM válido — es un cliente del programa lealtad sin RFC).
  const { contactId, fiscalEntityId } = splitCompoundId(id)
  const cur = await gqlClient.query(PosContactQuery, { id: contactId }).toPromise()
  if (cur.error) throw cur.error
  const current = cur.data?.contact ? graphToCrmContact(cur.data.contact as GraphContact) : null
  if (current === null) return
  const fiscals = current.fiscalEntities ?? []
  const next = fiscalEntityId
    ? fiscals.filter((f) => f.id !== fiscalEntityId)
    : fiscals.slice(1) // sin compound id legacy: borra la primera (default)
  // Borra UNA fiscalEntity via updateContact (reemplazo de la lista) — preserva
  // datos CRM/lealtad del Contact. tenant del JWT (guard IDOR).
  const res = await gqlClient.mutation(PosUpdateContactMutation, {
    id: contactId,
    input: { fiscalEntities: next },
  }).toPromise()
  if (res.error) throw res.error
}

/** Alias used by the P8 billing module — list saved fiscal customers (CFDI receivers). */
export async function listFiscalCustomers(search?: string, signal?: AbortSignal): Promise<CustomerDto[]> {
  return listCustomers(search, signal)
}

/** Create or update a fiscal customer. With `id` ⇒ patch; without ⇒ create. */
export async function upsertFiscalCustomer(input: CustomerInput & { id?: string }): Promise<CustomerDto> {
  const { id, ...rest } = input
  return id ? updateCustomer(id, rest) : createCustomer(rest)
}

// Test helpers (do not import from runtime code).
export function __resetCustomersStub(): void {
  stub = null
}
